import { h, render, Component, Fragment } from 'preact';
import { LayoutProvider, useLayoutState } from './LayoutContext';
import { PanelHeader } from './PanelHeader';
import { debounce } from './debounce';
import { GeneralPropertiesProvider } from './providers/GeneralPropertiesProvider';
import { GatewayPropertiesProvider } from './providers/GatewayPropertiesProvider';
import { TransitionPropertiesProvider } from './providers/TransitionPropertiesProvider';
import { ServiceTaskPropertiesProvider } from './providers/ServiceTaskPropertiesProvider';
import { ScriptTaskPropertiesProvider } from './providers/ScriptTaskPropertiesProvider';
import { TriggerPropertiesProvider } from './providers/TriggerPropertiesProvider';
import { BoundaryPropertiesProvider } from './providers/BoundaryPropertiesProvider';
import { SubProcessPropertiesProvider } from './providers/SubProcessPropertiesProvider';
import { MessageTaskPropertiesProvider } from './providers/MessageTaskPropertiesProvider';
import { CallActivityPropertiesProvider } from './providers/CallActivityPropertiesProvider';
import { ParticipantPropertiesProvider } from './providers/ParticipantPropertiesProvider';
import { LanePropertiesProvider } from './providers/LanePropertiesProvider';
import { DataPropertiesProvider } from './providers/DataPropertiesProvider';
import { TextAnnotationPropertiesProvider } from './providers/TextAnnotationPropertiesProvider';
import { LoopPropertiesProvider } from './providers/LoopPropertiesProvider';

const DEFAULT_PRIORITY = 1000;

// ── Group component with LayoutContext persistence & edit dot ─────────────────

function GroupSection({ group, element }) {
  const [isOpen, setIsOpen] = useLayoutState(['groups', group.id, 'open'], false);

  const toggle = () => setIsOpen(!isOpen);

  const edited = group.isEdited ? group.isEdited(element) : false;

  return (
    <div key={group.id} class={`epx-group ${isOpen ? 'epx-group--open' : ''}`}>
      <div class="epx-group-header" onClick={toggle}>
        <span class="epx-group-header-title">
          {group.label}
        </span>
        <span class="epx-group-header-actions">
          {edited && <span class="epx-edit-dot" />}
          {group.badge != null && (
            <span class="epx-badge">{group.badge}</span>
          )}
          <span class="epx-group-arrow">{isOpen ? '\u25BE' : '\u25B8'}</span>
        </span>
      </div>
      {isOpen && (
        <div class="epx-group-entries">
          {group.entries.map(entry => (
            <entry.component key={entry.id} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Panel content ─────────────────────────────────────────────────────────────

function PanelContent({ element, groups, i18n }) {
  if (!element) {
    return (
      <div class="epx-panel-empty">
        <svg class="epx-panel-empty-icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="8" y="6" width="24" height="30" rx="2" stroke="currentColor" stroke-width="2"/>
          <path d="M14 14h12M14 19h12M14 24h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <circle cx="36" cy="36" r="8" fill="currentColor" fill-opacity="0.1" stroke="currentColor" stroke-width="2"/>
          <path d="M33 36h6M36 33v6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <div class="epx-panel-empty-title">
          {i18n.noSelection || 'No selection'}
        </div>
        <div class="epx-panel-empty-hint">
          {i18n.selectElement || 'Click an element to view its properties'}
        </div>
      </div>
    );
  }

  return (
    <div class="epx-panel-body">
      {groups.map(group => (
        <GroupSection key={group.id} group={group} element={element} />
      ))}
    </div>
  );
}

// ── Main PropertiesPanel class ────────────────────────────────────────────────

export class PropertiesPanel {
  constructor(lf, container, i18n) {
    this._lf = lf;
    this._container = container;
    this._i18n = i18n;
    this._element = null;

    // Registry: array of { provider, priority } sorted by priority (higher first)
    this._registry = [];

    // Register built-in BPMN providers
    this._registerBuiltinProviders(lf, i18n);

    this._renderPanel();

    // Debounced re-render to avoid excessive updates during rapid changes
    this._debouncedRender = debounce(() => this._renderPanel(), 50);

    // Re-render when node properties change
    lf.on('node:properties-change', ({ id }) => {
      if (this._element && this._element.id === id) {
        const nodeData = lf.getNodeDataById(id);
        if (nodeData) this._element = nodeData;
        this._debouncedRender();
      }
    });

    // Re-render when edge properties change
    lf.on('edge:properties-change', ({ id }) => {
      if (this._element && this._element.id === id) {
        const edgeData = lf.getEdgeDataById(id);
        if (edgeData) this._element = edgeData;
        this._debouncedRender();
      }
    });
  }

  _registerBuiltinProviders(lf, i18n) {
    const builtins = [
      new GeneralPropertiesProvider(lf, i18n),
      new GatewayPropertiesProvider(lf, i18n),
      new TransitionPropertiesProvider(lf, i18n),
      new ServiceTaskPropertiesProvider(lf, i18n),
      new ScriptTaskPropertiesProvider(lf, i18n),
      new TriggerPropertiesProvider(lf, i18n),
      new BoundaryPropertiesProvider(lf, i18n),
      new SubProcessPropertiesProvider(lf, i18n),
      new MessageTaskPropertiesProvider(lf, i18n),
      new CallActivityPropertiesProvider(lf, i18n),
      new ParticipantPropertiesProvider(lf, i18n),
      new LanePropertiesProvider(lf, i18n),
      new DataPropertiesProvider(lf, i18n),
      new TextAnnotationPropertiesProvider(lf, i18n),
      new LoopPropertiesProvider(lf, i18n),
    ];
    for (const provider of builtins) {
      this.registerProvider(provider, DEFAULT_PRIORITY);
    }
  }

  /**
   * Register a properties provider.
   * @param {object} provider - Must implement isApplicable(type) and getGroups(element)
   * @param {number} [priority=1000] - Higher priority providers appear first
   */
  registerProvider(provider, priority = DEFAULT_PRIORITY) {
    this._registry.push({ provider, priority });
    // Sort descending by priority so higher-priority groups come first
    this._registry.sort((a, b) => b.priority - a.priority);
    if (this._element) this._renderPanel();
  }

  /**
   * Unregister a provider by reference or by its id property.
   * @param {object|string} providerOrId - The provider instance or its id
   */
  unregisterProvider(providerOrId) {
    this._registry = this._registry.filter(entry => {
      if (typeof providerOrId === 'string') {
        return entry.provider.id !== providerOrId;
      }
      return entry.provider !== providerOrId;
    });
    if (this._element) this._renderPanel();
  }

  show(element) {
    this._element = element;
    this._lf.emit('propertiesPanel.show', { element });
    this._renderPanel();
  }

  hide() {
    this._element = null;
    this._lf.emit('propertiesPanel.hide', {});
    this._renderPanel();
  }

  /**
   * Collect groups from all applicable providers using chain-of-responsibility.
   * Providers can either:
   * - Return an array of groups (legacy, appended)
   * - Return a function(groups) => groups (can modify/reorder/remove existing groups)
   */
  _getGroups() {
    if (!this._element) return [];
    const type = this._element.type;
    let groups = [];
    for (const { provider } of this._registry) {
      if (provider.isApplicable(type)) {
        const result = provider.getGroups(this._element, groups);
        if (typeof result === 'function') {
          // Chain pattern: provider transforms existing groups
          groups = result(groups);
        } else if (Array.isArray(result)) {
          // Legacy pattern: provider returns new groups to append
          groups.push(...result);
        }
      }
    }
    return groups;
  }

  _renderPanel() {
    const groups = this._getGroups();
    render(
      <LayoutProvider>
        <div class="epx-properties-panel">
          <PanelHeader element={this._element} i18n={this._i18n} />
          <PanelContent
            element={this._element}
            groups={groups}
            i18n={this._i18n}
          />
        </div>
      </LayoutProvider>,
      this._container
    );
  }

  destroy() {
    if (this._debouncedRender) this._debouncedRender.cancel();
    render(null, this._container);
  }
}
